import styled, { css } from 'styled-components';
import { screenMd } from 'constants/styles/breakpoints';

// Secondary/Promo Purple gradient applied as text fill (eyebrow, headline
// highlight, feature label and metric labels in the Figma design).
const gradientText = css`
  background: var(--secondary-promo-purple-gradient);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const Container = styled.div<{ $layout: 'split' | 'stacked' }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
  width: 100%;
  max-width: 943px;
  margin: 0 auto;
  box-sizing: border-box;

  ${screenMd} {
    flex-direction: ${({ $layout }) => ($layout === 'split' ? 'row' : 'column')};
    gap: ${({ $layout }) => ($layout === 'split' ? '46px' : '24px')};
    align-items: ${({ $layout }) => ($layout === 'split' ? 'flex-start' : 'stretch')};
  }
`;

export const ContentColumn = styled.div<{ $layout: 'split' | 'stacked' }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
  width: 100%;

  ${screenMd} {
    ${({ $layout }) =>
      $layout === 'split' &&
      css`
        width: 348px;
        flex: 0 0 348px;
      `}
  }
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 100px;
  background: rgba(211, 201, 250, 0.5);
`;

export const EyebrowLabel = styled.span`
  ${gradientText}
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
`;

export const Headline = styled.h2`
  margin: 0;
  font-size: 26px;
  font-weight: 900;
  line-height: 30px;
  color: ${({ theme }) => theme.default.colors.fonts.dark};
`;

export const HighlightedText = styled.span`
  ${gradientText}
`;

export const Description = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.default.colors.fonts.darkGray};
`;

export const CtaGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const Divider = styled.hr`
  width: 100%;
  margin: 0;
  border: none;
  border-top: 1px solid var(--dusty-gray);
`;

export const FeaturesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

export const FeatureListLabel = styled.span`
  ${gradientText}
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
`;

export const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const FeatureListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  line-height: 14px;
  color: ${({ theme }) => theme.default.colors.fonts.dark};
`;

export const CheckBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 21px;
  height: 21px;
  border-radius: 8px;
  background: #ede9fd;
  border: 2px solid rgba(237, 233, 253, 0.3);
`;

export const ArtworkColumn = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: center;
`;

// Positioning anchor that shrink-wraps the hero image, so the overlaid metric
// cards can be placed as percentages of the image box (matching Figma).
export const ArtworkFigure = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 480px;
`;

export const ArtworkImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

export const MetricsGrid = styled.div<{ $overlay?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  /* Figma: both cards share a horizontal centre at 79.4% of the image width and
     the pair is vertically centred at 69.9% of the image height (gap 11px). */
  ${({ $overlay }) =>
    $overlay &&
    css`
      position: absolute;
      top: 69.9%;
      left: 79.4%;
      transform: translate(-50%, -50%);
      flex-direction: column;
      flex-wrap: nowrap;
      align-items: center;
      gap: 11px;
    `}
`;

export const MetricCard = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-direction: ${({ $inline }) => ($inline ? 'row' : 'column')};
  align-items: ${({ $inline }) => ($inline ? 'center' : 'flex-start')};
  gap: 4px;
  padding: 20px;
  background: #fff;
  border: 1px solid var(--dusty-gray);
  border-radius: 16px;
  box-shadow: 0 0 7px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
`;

export const MetricLabel = styled.span`
  ${gradientText}
  font-size: 10px;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.8px;
  text-transform: uppercase;
`;

export const MetricValue = styled.span`
  font-size: 20px;
  font-weight: 900;
  line-height: 26px;
  color: ${({ theme }) => theme.default.colors.fonts.dark};
`;

export const Sparkline = styled.div`
  margin-top: 4px;
  line-height: 0;

  svg {
    display: block;
    width: 108px;
    height: auto;
  }
`;
